<template lang="pug">
div(ref="profileV2DraggableOptinStatus")
  div(v-for="(field, fieldIndex) in fields")
    v-layout(column).pl-1
      div This contact's current status is:
      v-layout.my-2(row align-center justify-center wrap)
        v-flex(grow)
          v-layout(row wrap)
            v-icon.mr-2(:style="field.style" small) {{field.icon}}
            div {{ field.value }}
        v-flex(shrink)
          v-tooltip(v-if="!consentExists && !showConsentForm" top max-width="300px")
            span Make sure a mobile phone number is saved to successfully opt-in your contact
            v-btn.white--text(
              small 
              color='primaryv2' 
              slot="activator"
              :class="{'ld-gated-feature': userIsCustomNumberGated}"
              @click="openConsentForm"
            ) Add opt-in consent
          v-btn(
            v-else-if="consentExists"
            small 
            outline
            flat
            color="primaryv2" 
            @click="showConsentForm = !showConsentForm"
          ) {{ showConsentForm ? 'Hide Consent' : 'View Consent' }} 
      v-divider
      v-slide-y-transition(hide-on-leave)
        div.mt-3.mb-2(v-show="showConsentForm")
          span How did your contact opt in?
          v-radio-group(v-model="type")
            v-radio(value="website")
              template(slot="label")
                span.body-2.font-weight-bold URL to my website consent form
            v-radio(value="paper")
              template(slot="label")
                span.body-2.font-weight-bold On paper or e-signature
          v-divider.mb-3
          v-form(ref="form" v-model="isFormValid")
            field-value.mb-3(
              v-if="type == 'website'"
              v-model="url"
              is-profile-v2
              hide-details
              field-label="URL*"
              field-placeholder="https://www.example.com"
              :validations="[required]"
            )
            v-input(
              v-else
              v-model="fileId"
              hide-details
              :rules="[required]"
            )
              v-btn.ma-0(
                small
                outline
                color="background"
                @click="openFileSelector"
              ) Choose File
              span.ml-2.body-2 {{ file ? file.name : 'Select a file to submit' }}
              input(
                ref="attachments"
                type="file"
                accept=".jpeg,.png,.pdf"
                style="display: none;"
                @change="handleFileSelect"
              )
            field-value.mt-4(
              v-model="description"
              is-profile-v2
              hide-details
              field-label="Description*"
              field-placeholder="Briefly describe your opt-in process."
              :validations="[required]"
            )
            field-value.mt-3(
              v-model="optinDate"
              is-profile-v2
              full-width
              hide-details
              field-type="date"
              field-label="Opted in date*"
              :validations="[required]"
            )
          v-layout.mt-2(row justify-end)
            v-btn(small flat @click="handleCancel") Cancel
            v-btn.white--text(
              small 
              color='primaryv2' 
              :class="{'ld-gated-feature': userIsCustomNumberGated}"
              :disabled="!isFormValid"
              @click="handleSave"
            ) {{ consentExists ? 'Edit' : 'Save' }}
</template>

<script>
import { feathers } from '@/feathers-client';
import { mapActions, mapGetters, mapState } from 'vuex';
import FieldValue from '../../profile/FieldValue'
import sendSMSGate from '@/Core/mixins/send-sms-gate';

export default {
  props: {
    card: {
      required: true,
      default: {},
    },
  },
  components: {
    FieldValue,
  },
  mixins: [sendSMSGate],
  data() {
    return {
      showConsentForm: false,
      type: 'website',
      url: '',
      description: '',
      optinDate: '',
      file: null,
      fileId: 0,
      isFormValid: false,
      required: v => !!v || 'This field is required',
    };
  },
  mounted() {
    this.resetForm();
  },
  watch: {
    type: {
      handler() {
        this.$refs.form[0].resetValidation();
      },
    },
    consent: {
      handler() {
        this.resetForm();
      },
      deep: true,
    },
  },
  computed: {
    ...mapState('smsConsent', ['consent']),
    ...mapGetters('userInformation', ['userHasFeatureFlag']),
    fields() {
      return this.card.fields;
    },
    consentExists() {
      return this.consent.attempts > 0;
    },
    userIsCustomNumberGated() {
      return this.gatedAction === this.gatedActions.SetCustomNumber;
    },
  },
  methods: {
    ...mapActions('smsConsent', ['submitConsent']),
    handleCancel() {
      this.showConsentForm = false;
      this.resetForm();
    },
    openFileSelector() {
      this.$refs.attachments[0].click();
    },
    async handleFileSelect({ target }) {
      if (!target || !target.files || !target.files.length) return;
      this.file = target.files[0];
      this.fileId = 0;
      await this.uploadFile();
    },
    async uploadFile() {
      const formData = new FormData();
      formData.set('attachments', this.file);
      formData.set('folder_id', 0);
      try {
        const response = await feathers
          .service('files')
          .create(formData);

        this.fileId = response.id;
      } catch (err) {
        this.file = null;
      }
    },
    async handleSave() {
      if(this.userIsCustomNumberGated) return this.$emit('show-custom-number-modal');

      if (!this.$refs.form[0].validate()) return;

      const payload = {
        "contact_id": +this.$route.params.id,
        "form_type": this.type,
        "website_url": this.url,
        "description": this.description,
        "opted_in_date": new Date(this.optinDate),
        "file_id": this.fileId,
      };

      await this.submitConsent(payload);
      this.$emit('refresh-optin');
      this.showConsentForm = false;
    },
    resetForm() {
      if (!this.consentExists) {
        this.type = 'website';
        this.url = '';
        this.description = '';
        this.optinDate = '';
        this.fileId = 0;
        this.$refs.form[0].reset();
        return;
      }

      this.type = this.consent.form_type;
      this.url = this.consent.url;
      this.description = this.consent.description;
      this.optinDate = this.consent.opt_in_date;
      this.fileId = this.consent.file_id;
    },
    openConsentForm() {
      if(this.userIsCustomNumberGated) return this.$emit('show-custom-number-modal');

      this.showConsentForm = true;
    },
  },
}
</script>
